import React, { useState } from 'react';
import { CharacterCustomization, Race, Class, Outfit, Gender, HairTexture } from '@/types/game';
import { SKIN_COLORS, HAIR_STYLES, HAIR_TEXTURES, HAIR_COLORS, EYE_COLORS, GENDERS, OUTFIT_COLORS, MAKEUP_COLORS } from '@/data/gameData';
import { useGameStore } from '@/store/gameStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CharacterPreview3D } from './CharacterPreview3D';

const RACES: Race[] = ['dark-elf', 'mage', 'magician', 'bosmer'];
const CLASSES: Class[] = ['wendigo', 'bosmer', 'warrior', 'rogue'];
const OUTFITS: Outfit[] = ['cute', 'mystic', 'warrior', 'elegant'];
const GENDERS_ARRAY = GENDERS;
const WEAPONS = ['Sword', 'Staff', 'Dagger', 'Bow', 'Wand', 'Spear'];
const ACCESSORIES = ['Amulet', 'Ring', 'Crown', 'Circlet', 'Bracers', 'Cloak'];
const MOUTH_STYLES = ['Neutral', 'Smile', 'Confident', 'Mysterious'];
const FACE_MARKINGS = ['None', 'Tattoos', 'Scars', 'Glowing Runes', 'War Paint'];
const BUST_SIZES = ['small', 'medium', 'large', 'xl'] as const;
const WAIST_SIZES = ['petite', 'slim', 'average', 'curvy'] as const;
const LIP_STYLES = ['thin', 'natural', 'full', 'plump'] as const;
const EYE_SHAPES = ['almond', 'round', 'cat', 'wide'] as const;

export const CharacterCustomizer: React.FC = () => {
  const { setScreen, setCharacter } = useGameStore();
  const [customization, setCustomization] = useState<CharacterCustomization>({
    race: 'dark-elf',
    class: 'warrior',
    gender: 'female',
    skinColor: 'Coco Brown',
    hairStyle: 'locs',
    hairTexture: '4c',
    hairColor: 'Pale Teal',
    eyeColor: 'Amber',
    outfit: 'mystic',
    outfitColor: '#663399',
    makeupColor: '#FFB6D9',
    weapon: 'Sword',
    accessories: [],
    mouthStyle: 'Neutral',
    faceMarkings: 'None',
    bustSize: 'medium',
    waistSize: 'average',
    lipStyle: 'natural',
    eyeShape: 'almond',
  });

  const handleGenderChange = (gender: Gender) => {
    setCustomization({ ...customization, gender });
  };

  const handleRaceChange = (race: Race) => {
    setCustomization({ ...customization, race });
  };

  const handleClassChange = (clazz: Class) => {
    setCustomization({ ...customization, class: clazz });
  };

  const handleSkinColorChange = (skinColor: string) => {
    setCustomization({ ...customization, skinColor });
  };

  const handleHairStyleChange = (hairStyle: string) => {
    setCustomization({ ...customization, hairStyle });
  };

  const handleHairTextureChange = (hairTexture: HairTexture) => {
    setCustomization({ ...customization, hairTexture });
  };

  const handleHairColorChange = (hairColor: string) => {
    setCustomization({ ...customization, hairColor });
  };

  const handleOutfitColorChange = (outfitColor: string) => {
    setCustomization({ ...customization, outfitColor });
  };

  const handleMakeupColorChange = (makeupColor: string) => {
    setCustomization({ ...customization, makeupColor });
  };

  const handleEyeColorChange = (eyeColor: string) => {
    setCustomization({ ...customization, eyeColor });
  };

  const handleOutfitChange = (outfit: Outfit) => {
    setCustomization({ ...customization, outfit });
  };

  const handleWeaponChange = (weapon: string) => {
    setCustomization({ ...customization, weapon });
  };

  const handleAccessoryToggle = (accessory: string) => {
    const accessories = customization.accessories.includes(accessory)
      ? customization.accessories.filter((a) => a !== accessory)
      : [...customization.accessories, accessory];
    setCustomization({ ...customization, accessories });
  };

  const handleMouthChange = (mouthStyle: string) => {
    setCustomization({ ...customization, mouthStyle });
  };

  const handleFaceMarkingsChange = (faceMarkings: string) => {
    setCustomization({ ...customization, faceMarkings });
  };

  const handleBustSizeChange = (bustSize: 'small' | 'medium' | 'large' | 'xl') => {
    setCustomization({ ...customization, bustSize });
  };

  const handleWaistSizeChange = (waistSize: 'petite' | 'slim' | 'average' | 'curvy') => {
    setCustomization({ ...customization, waistSize });
  };

  const handleLipStyleChange = (lipStyle: 'thin' | 'natural' | 'full' | 'plump') => {
    setCustomization({ ...customization, lipStyle });
  };

  const handleEyeShapeChange = (eyeShape: 'almond' | 'round' | 'cat' | 'wide') => {
    setCustomization({ ...customization, eyeShape });
  };

  const handleStartGame = () => {
    setCharacter(customization);
    setScreen('game');
  };

  return (
    <div className="w-full h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black overflow-auto">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-4xl font-bold text-center text-orange-500 mb-8">Create Your Character</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Preview Section */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-slate-800 border-orange-500 h-full">
              <div className="w-full h-96">
                <CharacterPreview3D customization={customization} />
              </div>
              <div className="mt-4 space-y-2 text-white text-sm">
                <p>
                  <strong>Race:</strong> {customization.race}
                </p>
                <p>
                  <strong>Class:</strong> {customization.class}
                </p>
                <p>
                  <strong>Outfit:</strong> {customization.outfit}
                </p>
              </div>
            </Card>
          </div>

          {/* Customization Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Gender Selection */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Gender</h3>
              <div className="grid grid-cols-2 gap-2">
                {GENDERS_ARRAY.map((gender) => (
                  <Button
                    key={gender.id}
                    onClick={() => handleGenderChange(gender.id as Gender)}
                    variant={customization.gender === gender.id ? 'default' : 'outline'}
                    className={`${
                      customization.gender === gender.id
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {gender.name}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Race Selection */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Race</h3>
              <div className="grid grid-cols-2 gap-2">
                {RACES.map((race) => (
                  <Button
                    key={race}
                    onClick={() => handleRaceChange(race)}
                    variant={customization.race === race ? 'default' : 'outline'}
                    className={`capitalize ${
                      customization.race === race
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {race.replace('-', ' ')}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Class Selection */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Class</h3>
              <div className="grid grid-cols-2 gap-2">
                {CLASSES.map((clazz) => (
                  <Button
                    key={clazz}
                    onClick={() => handleClassChange(clazz)}
                    variant={customization.class === clazz ? 'default' : 'outline'}
                    className={`capitalize ${
                      customization.class === clazz
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {clazz}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Skin Color */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Skin Color</h3>
              <div className="grid grid-cols-4 gap-2">
                {SKIN_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => handleSkinColorChange(color.name)}
                    className={`w-full h-12 rounded border-2 transition ${
                      customization.skinColor === color.name
                        ? 'border-orange-500 scale-110'
                        : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
            </Card>

            {/* Hair Style */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Hair Style</h3>
              <div className="grid grid-cols-3 gap-2">
                {HAIR_STYLES.map((style) => (
                  <Button
                    key={style.id}
                    onClick={() => handleHairStyleChange(style.id)}
                    variant={customization.hairStyle === style.id ? 'default' : 'outline'}
                    className={`${
                      customization.hairStyle === style.id
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {style.name}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Hair Texture */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Hair Texture</h3>
              <div className="grid grid-cols-3 gap-2">
                {HAIR_TEXTURES.map((texture) => (
                  <Button
                    key={texture.id}
                    onClick={() => handleHairTextureChange(texture.id)}
                    variant={customization.hairTexture === texture.id ? 'default' : 'outline'}
                    className={`${
                      customization.hairTexture === texture.id
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {texture.name}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Hair Color */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Hair Color</h3>
              <div className="grid grid-cols-4 gap-2">
                {HAIR_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => handleHairColorChange(color.name)}
                    className={`w-full h-12 rounded border-2 transition ${
                      customization.hairColor === color.name
                        ? 'border-orange-500 scale-110'
                        : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
            </Card>

            {/* Eye Color */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Eye Color</h3>
              <div className="grid grid-cols-4 gap-2">
                {EYE_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => handleEyeColorChange(color.name)}
                    className={`w-full h-12 rounded border-2 transition ${
                      customization.eyeColor === color.name
                        ? 'border-orange-500 scale-110'
                        : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
            </Card>

            {/* Outfit Selection */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Outfit</h3>
              <div className="grid grid-cols-4 gap-2">
                {OUTFITS.map((outfit) => (
                  <Button
                    key={outfit}
                    onClick={() => handleOutfitChange(outfit)}
                    variant={customization.outfit === outfit ? 'default' : 'outline'}
                    className={`capitalize ${
                      customization.outfit === outfit
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {outfit}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Outfit Color Wheel */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Outfit Color</h3>
              <div className="grid grid-cols-4 gap-2">
                {OUTFIT_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => handleOutfitColorChange(color.hex)}
                    className={`w-full h-12 rounded border-2 transition flex flex-col items-center justify-center ${
                      customization.outfitColor === color.hex
                        ? 'border-white scale-110'
                        : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  >
                    {customization.outfitColor === color.hex && (
                      <span className="text-white text-xs font-bold">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </Card>

            {/* Makeup Color Wheel */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Makeup Color</h3>
              <div className="grid grid-cols-4 gap-2">
                {MAKEUP_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => handleMakeupColorChange(color.hex)}
                    className={`w-full h-12 rounded border-2 transition flex flex-col items-center justify-center ${
                      customization.makeupColor === color.hex
                        ? 'border-white scale-110'
                        : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  >
                    {customization.makeupColor === color.hex && (
                      <span className="text-white text-xs font-bold">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </Card>

            {/* Mouth Style */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Mouth Style</h3>
              <div className="grid grid-cols-2 gap-2">
                {MOUTH_STYLES.map((style) => (
                  <Button
                    key={style}
                    onClick={() => handleMouthChange(style)}
                    variant={customization.mouthStyle === style ? 'default' : 'outline'}
                    className={`${
                      customization.mouthStyle === style
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {style}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Face Markings */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Face Markings</h3>
              <div className="grid grid-cols-3 gap-2">
                {FACE_MARKINGS.map((marking) => (
                  <Button
                    key={marking}
                    onClick={() => handleFaceMarkingsChange(marking)}
                    variant={customization.faceMarkings === marking ? 'default' : 'outline'}
                    className={`${
                      customization.faceMarkings === marking
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {marking}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Weapon Selection */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Primary Weapon</h3>
              <div className="grid grid-cols-3 gap-2">
                {WEAPONS.map((weapon) => (
                  <Button
                    key={weapon}
                    onClick={() => handleWeaponChange(weapon)}
                    variant={customization.weapon === weapon ? 'default' : 'outline'}
                    className={`${
                      customization.weapon === weapon
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {weapon}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Accessories */}
            <Card className="p-4 bg-slate-800 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Accessories</h3>
              <div className="grid grid-cols-3 gap-2">
                {ACCESSORIES.map((accessory) => (
                  <Button
                    key={accessory}
                    onClick={() => handleAccessoryToggle(accessory)}
                    variant={customization.accessories.includes(accessory) ? 'default' : 'outline'}
                    className={`${
                      customization.accessories.includes(accessory)
                        ? 'bg-orange-600 border-orange-600'
                        : 'border-gray-600 text-gray-300'
                    }`}
                  >
                    {accessory}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Body Customization Section */}
            <div className="col-span-full border-t border-orange-500 pt-6 mt-6">
              <h2 className="text-2xl font-bold text-orange-500 mb-6">Body Customization</h2>
              
              {/* Bust Size */}
              <Card className="p-4 bg-slate-800 border-orange-500 mb-4">
                <h3 className="text-xl font-bold text-orange-500 mb-3">Bust Size</h3>
                <div className="grid grid-cols-4 gap-2">
                  {BUST_SIZES.map((size) => (
                    <Button
                      key={size}
                      onClick={() => handleBustSizeChange(size)}
                      variant={customization.bustSize === size ? 'default' : 'outline'}
                      className={`capitalize ${
                        customization.bustSize === size
                          ? 'bg-pink-600 border-pink-600'
                          : 'border-gray-600 text-gray-300'
                      }`}
                    >
                      {size}
                    </Button>
                  ))}
                </div>
              </Card>

              {/* Waist Size */}
              <Card className="p-4 bg-slate-800 border-orange-500 mb-4">
                <h3 className="text-xl font-bold text-orange-500 mb-3">Waist Size</h3>
                <div className="grid grid-cols-4 gap-2">
                  {WAIST_SIZES.map((size) => (
                    <Button
                      key={size}
                      onClick={() => handleWaistSizeChange(size)}
                      variant={customization.waistSize === size ? 'default' : 'outline'}
                      className={`capitalize ${
                        customization.waistSize === size
                          ? 'bg-pink-600 border-pink-600'
                          : 'border-gray-600 text-gray-300'
                      }`}
                    >
                      {size}
                    </Button>
                  ))}
                </div>
              </Card>

              {/* Lip Style */}
              <Card className="p-4 bg-slate-800 border-orange-500 mb-4">
                <h3 className="text-xl font-bold text-orange-500 mb-3">Lip Style</h3>
                <div className="grid grid-cols-4 gap-2">
                  {LIP_STYLES.map((style) => (
                    <Button
                      key={style}
                      onClick={() => handleLipStyleChange(style)}
                      variant={customization.lipStyle === style ? 'default' : 'outline'}
                      className={`capitalize ${
                        customization.lipStyle === style
                          ? 'bg-pink-600 border-pink-600'
                          : 'border-gray-600 text-gray-300'
                      }`}
                    >
                      {style}
                    </Button>
                  ))}
                </div>
              </Card>

              {/* Eye Shape */}
              <Card className="p-4 bg-slate-800 border-orange-500 mb-4">
                <h3 className="text-xl font-bold text-orange-500 mb-3">Eye Shape</h3>
                <div className="grid grid-cols-4 gap-2">
                  {EYE_SHAPES.map((shape) => (
                    <Button
                      key={shape}
                      onClick={() => handleEyeShapeChange(shape)}
                      variant={customization.eyeShape === shape ? 'default' : 'outline'}
                      className={`capitalize ${
                        customization.eyeShape === shape
                          ? 'bg-pink-600 border-pink-600'
                          : 'border-gray-600 text-gray-300'
                      }`}
                    >
                      {shape}
                    </Button>
                  ))}
                </div>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="col-span-full grid grid-cols-2 gap-4 pt-6">
              <Button
                onClick={() => setScreen('title')}
                className="py-3 text-lg font-bold bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Back
              </Button>
              <Button
                onClick={handleStartGame}
                className="py-3 text-lg font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
              >
                Start Game
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};